import React, { useState } from 'react';
import axios from 'axios';

function PhotoUploadModal({ 
    isOpen, 
    onClose, 
    patientContext, 
    onUploadSuccess 
}) {
    // HELPER: Get Today's Date in LOCAL TIME (Corrects the timezone bug)
    const getTodayLocal = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [selectedFiles, setSelectedFiles] = useState([]);
    // Use the helper here
    const [visitDate, setVisitDate] = useState(getTodayLocal()); 
    const [doctorName, setDoctorName] = useState('');
    const [notes, setNotes] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    // Handle file selection
    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
    
        // 🔍 Validate: Allow images AND PDFs
        const validFiles = files.filter(file => 
            file.type.startsWith('image/') || file.type === 'application/pdf'
        );

        if (validFiles.length < files.length) {
            setError('Some files were ignored. Only Images and PDFs are supported.');
        }

        setSelectedFiles(validFiles);
        console.log('📁 Files ready for upload:', validFiles.map(f => f.name));
        };

    // Remove file from selection
    const removeFile = (index) => {
        const updatedFiles = selectedFiles.filter((_, i) => i !== index);
        setSelectedFiles(updatedFiles);
    };

    // Format file size for display
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Validate form
    const isFormValid = () => {
        return selectedFiles.length > 0 && visitDate;
    };

    // Handle upload
    const handleUpload = async () => {
        if (!isFormValid()) {
            setError('Please select at least one photo and specify a date');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            // Prepare form data
            const formData = new FormData();
            
            // Add files
            selectedFiles.forEach(file => {
                formData.append('photos', file);
            });
            
            // Add other data
            formData.append('visitDate', visitDate);
            if (doctorName.trim()) {
                formData.append('doctorName', doctorName.trim());
            }
            if (notes.trim()) {
                formData.append('notes', notes.trim());
            }

            console.log('📤 Uploading photos:', {
                patientId: patientContext.branchHN,
                fileCount: selectedFiles.length,
                visitDate: visitDate,
                doctorName: doctorName || 'Not specified',
                notes: notes || 'No notes'
            });

            // Upload to backend
            const response = await axios.post(
                `/api/patient/${patientContext.branchHN}/upload-photos`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 60000 // 60 second timeout for large files
                }
            );

            console.log('✅ Upload successful:', response.data);

            if (response.data.success) {
                // Notify parent component
                if (onUploadSuccess) {
                    onUploadSuccess({
                        visitCount: response.data.visits.length,
                        visitDate: visitDate,
                        patientId: patientContext.branchHN,
                        visits: response.data.visits
                    });
                }

                // Reset form
                setSelectedFiles([]);
                setDoctorName('');
                setNotes('');
                setVisitDate(getTodayLocal()); // Reset to today (local time)
                
                // Close modal
                onClose();
            } else {
                setError('Upload failed: ' + (response.data.error || 'Unknown error'));
            }

        } catch (error) {
            console.error('❌ Upload error:', error);
            
            let errorMessage = 'Upload failed';
            if (error.response?.status === 413) {
                errorMessage = 'Files too large. Maximum size is 10MB per file.';
            } else if (error.response?.status === 400) {
                errorMessage = error.response.data.error || 'Invalid file format';
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = 'Upload timeout. Please try with smaller files.';
            } else if (error.code === 'ERR_NETWORK') {
                errorMessage = 'Network error. Check your connection.';
            } else {
                errorMessage = error.response?.data?.error || error.message;
            }
            
            setError(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    // Handle close
    const handleClose = () => {
        if (!uploading) {
            setSelectedFiles([]);
            setError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '600px',
                maxHeight: '90vh',
                width: '90%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: '#fff3e0'
                }}>
                    <h2 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '24px', 
                        color: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        📸 Upload Photos
                    </h2>
                    <div style={{ 
                        fontSize: '16px', 
                        color: '#666' 
                    }}>
                        Patient: {patientContext.name} - {patientContext.branchHN}
                    </div>
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '20px'
                }}>
                    {/* Date Selection */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '8px'
                        }}>
                            📅 Visit Date:
                        </label>
                        <input
                            type="date"
                            value={visitDate}
                            onChange={(e) => setVisitDate(e.target.value)}
                            disabled={uploading}
                            style={{
                                width: '100%',
                                padding: '12px',
                                fontSize: '16px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '6px',
                                outline: 'none',
                                backgroundColor: uploading ? '#f5f5f5' : 'white'
                            }}
                            // FIXED: Use Local Time for Max Date
                            max={getTodayLocal()} 
                        />
                        <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginTop: '4px' 
                        }}>
                            Select the date when the handwritten OPD was actually created
                        </div>
                    </div>

                    {/* File Upload */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '8px'
                        }}>
                            📁 Select Photos:
                        </label>
                        <input
                            type="file"
                            multiple
                            accept="image/*,application/pdf"
                            onChange={handleFileSelect}
                            disabled={uploading}
                            style={{
                                width: '100%',
                                padding: '12px',
                                fontSize: '16px',
                                border: '2px dashed #FF9800',
                                borderRadius: '6px',
                                outline: 'none',
                                backgroundColor: uploading ? '#f5f5f5' : '#fff3e0',
                                cursor: uploading ? 'not-allowed' : 'pointer'
                            }}
                        />
                        <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginTop: '4px' 
                        }}>
                            Supported formats: JPG, PNG, PDF. Max 10MB per file. Select multiple files at once.
                        </div>
                    </div>

                    {/* Selected Files List */}
                    {selectedFiles.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                fontSize: '16px', 
                                fontWeight: 'bold',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                📋 Selected Files ({selectedFiles.length}):
                            </div>
                            <div style={{
                                maxHeight: '150px',
                                overflowY: 'auto',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                padding: '10px'
                            }}>
                                {selectedFiles.map((file, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '8px',
                                        marginBottom: '5px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ 
                                                fontSize: '14px', 
                                                fontWeight: 'bold',
                                                color: '#333'
                                            }}>
                                                {file.name}
                                            </div>
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: '#666' 
                                            }}>
                                                {formatFileSize(file.size)}
                                            </div>
                                        </div>
                                        {!uploading && (
                                            <button
                                                onClick={() => removeFile(index)}
                                                style={{
                                                    padding: '4px 8px',
                                                    fontSize: '12px',
                                                    backgroundColor: '#f44336',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ✖️
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Doctor Name */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '8px'
                        }}>
                            👨‍⚕️ Doctor Name (Optional):
                        </label>
                        <input
                            type="text"
                            value={doctorName}
                            onChange={(e) => setDoctorName(e.target.value)}
                            disabled={uploading}
                            placeholder="Enter doctor name"
                            style={{
                                width: '100%',
                                padding: '12px',
                                fontSize: '16px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '6px',
                                outline: 'none',
                                backgroundColor: uploading ? '#f5f5f5' : 'white'
                            }}
                        />
                    </div>

                    {/* Notes */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '8px'
                        }}>
                            📝 Notes (Optional):
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={uploading}
                            placeholder="Additional notes about these photos"
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '12px',
                                fontSize: '16px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '6px',
                                outline: 'none',
                                backgroundColor: uploading ? '#f5f5f5' : 'white',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div style={{
                            padding: '15px',
                            marginBottom: '20px',
                            backgroundColor: '#ffebee',
                            border: '1px solid #f44336',
                            borderRadius: '6px',
                            color: '#c62828',
                            fontSize: '14px'
                        }}>
                            ❌ {error}
                        </div>
                    )}

                    {/* Upload Progress */}
                    {uploading && (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            backgroundColor: '#e3f2fd',
                            borderRadius: '6px',
                            marginBottom: '20px'
                        }}>
                            <div style={{ fontSize: '40px', marginBottom: '10px' }}>📤</div>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1976d2' }}>
                                Uploading {selectedFiles.length} photo(s)...
                            </div>
                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                Please wait, this may take a moment for large files
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '20px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px'
                }}>
                    <button
                        onClick={handleClose}
                        disabled={uploading}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: uploading ? '#ccc' : '#9E9E9E',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: uploading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={!isFormValid() || uploading}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: (!isFormValid() || uploading) ? '#ccc' : '#FF9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (!isFormValid() || uploading) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {uploading ? '📤 Uploading...' : `📸 Upload ${selectedFiles.length} Photo(s)`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PhotoUploadModal;